/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent } from 'react';
import { Modal, Offcanvas } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPaste } from '@fortawesome/free-solid-svg-icons';
import { useState, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import installmentService from 'service/installment';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import InstallmentAddTable from './InstallmentAddTable';
import UpdateInstallemnt from './UpdateInstallemnt';

export const installmentBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'installment',
    url: '/installment'
  },
  {
    label: 'products',
    active: true
  }
];

export type InstallmentType = {
  name: string;
  barcode: string;
  price: string;
  status: string;
  gupid: number;
};

const Installment = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [openInstallment, setOpenInstallment] = useState(false);

  const [openUpdateInstallment, setOpenUpdateInstallment] = useState(false);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<any>(0);

  //Get installment
  const { data, refetch } = useQuery(['GET_INSTALLMET'], () => {
    const res = installmentService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => res?.items);
    return res;
  });

  const installmentData: InstallmentType[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          name: items?.name,
          barcode: items?.barcode,
          price: items?.price,
          currency: items?.currency?.name?.[i18n?.language],
          status: items?.status === true ? t('active') : t('blocked'),
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const installmentTableColumns: ColumnDef<InstallmentType>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div className="d-flex gap-2">
          <Button
            variant="hover"
            className="p-2"
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(
                `/installment/installment-plan/${rowData?.row?.original?.gupid}`,
                {
                  state: {
                    price: rowData?.row?.original?.price
                  }
                }
              );
            }}
          >
            <FontAwesomeIcon icon={faPaste} className="fs-7" />
          </Button>

          <Button
            className="p-2"
            variant="hover"
            onClick={() => {
              setOpenUpdateInstallment(true);
              setSelectedInstallmentId(rowData?.row?.original?.gupid);
            }}
          >
            <FeatherIcon icon="edit-2" className="cursor-pointer" size={20} />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price',
      header: t('price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          [t('active')]: { bg: '#BBFBD0', text: '#119C2B' },
          [t('blocked')]: { bg: '#FDDFDD', text: '#F2271C' }
        };
        /* prettier-ignore */
        const backgroundColor =
         defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex">
            <p
              style={{
                background: backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {status}
            </p>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: installmentData,
    columns: installmentTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <>
      <PageBreadcrumb items={installmentBreadcrumbItems} />

      <div className="mb-9">
        <h2 className="mb-5">{t('products')}</h2>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex justify-content-between gap-2">
              <SearchBox
                placeholder={t('search')}
                onChange={handleSearchInputChange}
              />
              <div className="d-flex justify-content-between gap-2">
                <Button
                  className="d-flex align-items-center gap-2"
                  variant="primary"
                  onClick={() => setOpenInstallment(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {t('add')}
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>

          {/* <SupperModal
            show={openSupperModal}
            setShow={setOpenSupperModal}
            setConfirm={setConfirmToDelete}
            title={t('installment')}
            bodyText={t('you_want_delete')}
          /> */}

          <Modal
            show={openInstallment}
            onHide={() => {
              setOpenInstallment(false);
              refetch();
            }}
            size="xl"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>{t('products')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <InstallmentAddTable />
            </Modal.Body>
          </Modal>

          <Offcanvas
            show={openUpdateInstallment}
            onHide={() => {
              setOpenUpdateInstallment(false);
              setSelectedInstallmentId(0);
            }}
            placement="end"
            backdrop="static"
            keyboard={false}
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
                {t('changed')}
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <UpdateInstallemnt
                setOpenUpdateInstallment={setOpenUpdateInstallment}
                refetch={refetch}
                selectedInstallmentId={selectedInstallmentId}
                setSelectedInstallmentId={setSelectedInstallmentId}
              />
            </Offcanvas.Body>
          </Offcanvas>
        </AdvanceTableProvider>
      </div>
    </>
  );
};

export default Installment;
